// Lightweight feature-flag hook.
//
// Resolves flags from sensible defaults per vertical, then Vite environment
// variables (VITE_FEATURE_<NAME>=true|false). On top of that, GET /auth/me
// reports `enabled_features` — the flags the API actually serves this tenant
// (tenant flag OR API env, FeatureFlagGuard's rule) — and any API flag missing
// from it is switched off, so no screen is offered whose calls are all refused.
//
// Flags are keyed identically to CLAUDE.md: ENABLE_KDS, ENABLE_FESTIVE_MODULE,
// ENABLE_VOICE_POS, ENABLE_CREDIT_BILLING, ENABLE_DISPATCH,
// ENABLE_MATERIAL_INVENTORY.

import { useMemo } from 'react';
import { useAuthStore } from '@/stores/use-auth-store';

export type FeatureFlagMap = Record<string, boolean>;

const ALL_FLAGS = [
  'ENABLE_KDS',
  'ENABLE_FESTIVE_MODULE',
  'ENABLE_VOICE_POS',
  'ENABLE_CREDIT_BILLING',
  'ENABLE_DISPATCH',
  'ENABLE_MATERIAL_INVENTORY',
  'ENABLE_RESTAURANT',
] as const;

type KnownFlag = (typeof ALL_FLAGS)[number];

// The flags the API knows (tenants.feature_flags / env). ENABLE_RESTAURANT is
// client-only — derived from the vertical — so the API never reports it.
const API_FLAGS: readonly KnownFlag[] = ALL_FLAGS.filter((f) => f !== 'ENABLE_RESTAURANT');

function envFlag(name: KnownFlag): boolean | undefined {
  const raw = (import.meta.env as Record<string, string | undefined>)[`VITE_FEATURE_${name}`];
  if (raw === undefined) return undefined;
  return raw === 'true' || raw === '1';
}

export function useFeatureFlag(): FeatureFlagMap {
  const businessType = useAuthStore((s) => s.profile?.tenant?.business_type);
  const enabledFeatures = useAuthStore((s) => s.profile?.enabled_features);

  return useMemo(() => {
    const isRestaurant = businessType === 'RESTAURANT';
    const defaults: FeatureFlagMap = {
      ENABLE_KDS: isRestaurant,
      ENABLE_FESTIVE_MODULE: isRestaurant,
      ENABLE_VOICE_POS: false,
      ENABLE_CREDIT_BILLING: true,
      ENABLE_DISPATCH: isRestaurant,
      // Reserved for Phase 2 (FR-FLAG-006) — off, matching the API default.
      ENABLE_MATERIAL_INVENTORY: false,
      // Restaurant-vertical operations (Tables, Floor Plan, Reservations,
      // Recipes, Menus, Modifiers, Combos). Hidden for RETAIL tenants.
      ENABLE_RESTAURANT: isRestaurant,
    };
    // Environment overrides take precedence when provided.
    for (const name of ALL_FLAGS) {
      const override = envFlag(name);
      if (override !== undefined) defaults[name] = override;
    }
    // The API is the authority on its own gates. Until the profile loads the
    // defaults stand (a guarded page may mount, then redirect).
    if (enabledFeatures) {
      for (const name of API_FLAGS) {
        if (!enabledFeatures.includes(name)) defaults[name] = false;
      }
    }
    return defaults;
  }, [businessType, enabledFeatures]);
}
